/* eslint max-classes-per-file: 0 */

import {
  LkPublication as Data,
  LkPublicationCollection as Collection,
  LkPublicationStorage as Storage,
  LkPublicationStorageFileWithMetaCollection as StorageFileWithMetaCollection,
} from '@/typings/api/Publication'

import { Fabric } from '@/store/createCrudModule'
import { LkPublicationsAdapter } from './LkPublicationsAdapter'
import { FormTemplate } from '@/typings/api/FormTemplate'
import { BaseApiParams } from '@/typings/common'
import { CrudActionParams } from '@/store/createCrudModule/typings/typings'
import { Store } from 'vuex'
import { Context } from 'vuex-smart-module'
import { authStoreModule } from '@/views/Auth/store'
import { LkPublicPublicationsAdapter } from './LkPublicPublicationsAdapter'
import { LkMunMehAdapter } from './LkMunMehAdapter'
import { MunMeh, MunMehFormProgress } from '@/typings/api/MunMeh'


export const fabric = Fabric<Data, Collection>()

type S = LkPublicationsState
type G = LkPublicationsGetters
type M = LkPublicationsMutations
type A = LkPublicationsActions


type GetStorageFiles = {
  publicationId: Data['id'];
  storageId: Storage['id'];
  params?: BaseApiParams;
}

type DownloadStorageFile = GetStorageFiles & {
  storageFileId: number;
}

type MunMehFormsProgress = {
  munMehId: MunMeh['id'];
  progress: MunMehFormProgress;
}

export class LkPublicationsState extends fabric.State {
  public formTemplate: FormTemplate | null = null;

  public munMehProgress: MunMehFormsProgress[] = [];
}

export class LkPublicationsGetters extends fabric.Getters<S> {
  private store!: Store<unknown>;

  private auth!: Context<typeof authStoreModule>;

  public $init(store: Store<unknown>) {
    this.store = store
    this.auth = authStoreModule.context(store)
  }

  public get authorized() {
    return this.auth.getters.authorized
  }
}

export class LkPublicationsMutations extends fabric.Mutations<S> {
  public updateListData(payload: { slug: Data['slug']; data: Data }) {
    const { slug, data } = payload
    const { list } = this.state
    if (!list) return

    const index = list.findIndex((_) => _.slug === slug)

    if (index !== -1) {
      list.splice(index, 1, data)
    }
  }

  public removeFromList(id: Data['id']) {
    if (!this.state.list) return
    const index = this.state.list.findIndex((_) => _.id === id)
    if (index !== -1) this.state.list.splice(index, 1)
  }

  public setPublicationStarred(payload: { id: Data['id']; isStarred: boolean }) {
    const { id, isStarred } = payload
    const item = this.state.list?.find((_) => _.id === id)
    if (item) item.is_starred = isStarred
    if (this.state.single?.id === id) this.state.single.is_starred = isStarred
  }

  public setFormTemplate(payload?: FormTemplate) {
    if (!payload) return
    this.state.formTemplate = payload
  }

  public addMunMehProgress(data: MunMehFormsProgress) {
    this.state.munMehProgress.push(data)
  }
}

export class LkPublicationsActions extends fabric.Actions<S, G, M, A> {
  protected readonly adapter = new LkPublicationsAdapter();

  protected readonly publicAdapter = new LkPublicPublicationsAdapter();

  protected readonly munMehAdapter = new LkMunMehAdapter();

  protected readonly idName = 'id';

  public async getAll(
    params: Pick<Required<CrudActionParams>, 'payload'> & Omit<CrudActionParams, 'id'>,
  ): Promise<Collection[]> {
    const { authorized } = this.getters
    if (authorized) {
      return super.getAll(params)
    }

    const { payload } = params || {}
    const response = await this.publicAdapter.getAll(payload, params)

    const { data, meta } = response

    this.mutations.setList(data)
    this.mutations.setListMeta(meta as BaseApiParams)
    return this.state.list || data || []
  }

  public async getOneBySlug(params: Data) {
    const { authorized } = this.getters
    let data
    if (authorized) {
      data = await this.adapter.api.getOneBySlug(params.slug)

      // if (data.has_storage) {
      //   const storages = await this.adapter.api.getStorages(data.id)
      //   // @ts-ignore TODO:
      //   data.storages = await this.actions.getStoragesFiles({ publication: data, storages: storages.data })
      // }
      // if (data.has_data_collection) {
      //   const data_collections = await this.adapter.api.getDataCollections(data.id)
      //   // @ts-ignore TODO:
      //   data.data_collections = data_collections.data
      // }
      // if (data.has_munmeh) {
      //   const mun_meh = await this.munMehAdapter.api.getMunMeh(data.id)
      //   // @ts-ignore TODO:
      //   data.mun_meh = mun_meh.data
      // }
    } else {
      data = await this.publicAdapter.api.getOneBySlug(params.slug)
    }

    this.mutations.setOne(data)
    this.mutations.updateListData({ slug: params.slug, data })

    return data
  }

  public async getStorages(params: Data) {
    const result = await this.adapter.api.getStorages(params.id)

    const data = {
      ...params,
      storages: result.data,
    }

    this.mutations.setOne(data)
    this.mutations.updateListData({ slug: data.slug, data })

    return data
  }

  public async getStoragesFiles({ publication, storages }: { publication: Data; storages: Storage[] }) {
    const promises = storages.map((storage) => (
      this.adapter.api.getStorageFiles(publication.id, storage.id, { per_page: 12 })
    ))

    type ResponseType = [NonNullable<StorageFileWithMetaCollection>]

    // @ts-ignore TODO:
    const results = await Promise.all(promises) as ResponseType

    const result = storages.map((storage, index) => ({
      ...storage,
      files: results[index]?.files || results[index]?.data,
      count: results[index]?.count || 0,
    }))

    return result
  }

  public async getStorageFiles({ publicationId, storageId, params }: GetStorageFiles) {
    type ResponseType = {data: NonNullable<StorageFileWithMetaCollection>}

    const data = await this.adapter.api.getStorageFiles(publicationId, storageId, params) as ResponseType
    return data
  }

  public async getStorageFilesCount({ publicationId, storageId, params }: GetStorageFiles) {
    type ResponseType = { count: number }

    const data = await this.adapter.api.getStorageFilesCount(publicationId, storageId, params) as ResponseType
    return data.count
  }

  public async getMunMehFormsProgress(id: MunMeh['id']) {
    // const existed = this.state.munMehProgress.find(({ munMehId }) => munMehId === id)
    // if (existed) {
    //   return existed.progress
    // }

    const progress = await this.munMehAdapter.api.getMunMehFormsProgress(id)
    // this.mutations.addMunMehProgress({ munMehId: id, progress })
    return progress
  }

  public async downloadStorageFile({
    publicationId, storageId, storageFileId, params,
  }: DownloadStorageFile) {
    await this.adapter.api.downloadStorageFile(publicationId, storageId, storageFileId, params)
  }
}
